import { useCallback, useMemo, useState } from 'react';

/**
 * Local practice-mode state for the focused-view queue.
 *
 * Mirrors the semantics of the legacy `tysonMode` context
 * (`app/aida/contexts/tysonMode.tsx`) — an on/off toggle plus a set of
 * company ids already practiced this session — but as a plain `useState`
 * hook. Practice mode has a single consumer (the `FocusedLeadView` parent),
 * so per CLAUDE.md it is NOT wrapped in a Context/Provider.
 *
 * The data-fetching side (serving the next practice lead) lives in
 * `useNextLead({ practice: true, ... })`; this hook only owns the toggle and
 * the practiced-id bookkeeping.
 */
export interface PracticeModeState {
	/** Whether practice mode is currently active. */
	isActive: boolean;
	/** Company ids served during this practice session. */
	practicedCompanyIds: Set<string>;
	/** Activate practice mode. */
	enter(): void;
	/** Deactivate practice mode and clear the practiced-id set. */
	exit(): void;
	/** Record that `companyId` has been served as a practice lead. */
	recordPracticed(companyId: string): void;
}

export function usePracticeMode(): PracticeModeState {
	const [isActive, setIsActive] = useState(false);
	const [practicedCompanyIds, setPracticedCompanyIds] = useState<Set<string>>(() => new Set());

	// Stabilized per CLAUDE.md: the returned object + handlers feed `FocusedLeadView`'s
	// `fetchNextLead` useCallback dependency array, so fresh identities each render would
	// re-create that callback (and every downstream child prop) on every render.
	const enter = useCallback(() => {
		setIsActive(true);
	}, []);

	const exit = useCallback(() => {
		setIsActive(false);
		setPracticedCompanyIds(new Set());
	}, []);

	const recordPracticed = useCallback((companyId: string) => {
		setPracticedCompanyIds(prev => new Set(prev).add(companyId));
	}, []);

	return useMemo(
		() => ({ isActive, practicedCompanyIds, enter, exit, recordPracticed }),
		[isActive, practicedCompanyIds, enter, exit, recordPracticed]
	);
}
